import clsx from 'clsx';
import { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { BannerProps } from './types';

import { getStatusIcon } from '../../utils/getStatusIcon';
import { getStatusLiveRole } from '../../utils/getStatusLiveRole';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/subcomponents/IconButton';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { Text } from '../Text/Text';
import './Banner.css';

const messages = defineMessages({
  dismiss: {
    id: 'Banner.dismiss',
    defaultMessage: 'Dismiss',
    description: 'Accessible name for the banner dismiss button',
  },
  statusCautious: {
    id: 'Banner.statusCautious',
    defaultMessage: 'Caution',
    description: 'Accessible name for a cautious banner status icon',
  },
  statusCritical: {
    id: 'Banner.statusCritical',
    defaultMessage: 'Critical',
    description: 'Accessible name for a critical banner status icon',
  },
  statusInformational: {
    id: 'Banner.statusInformational',
    defaultMessage: 'Information',
    description: 'Accessible name for an informational banner status icon',
  },
  statusPositive: {
    id: 'Banner.statusPositive',
    defaultMessage: 'Success',
    description: 'Accessible name for a positive banner status icon',
  },
});

const statusIconLabels = {
  cautious: messages.statusCautious,
  critical: messages.statusCritical,
  informational: messages.statusInformational,
  positive: messages.statusPositive,
} as const;

export const Banner = memo(
  ({
    action,
    link,
    onDismiss,
    status = 'informational',
    text,
    title,
  }: BannerProps) => {
    const intl = useIntl();
    const dismissLabel = intl.formatMessage(messages.dismiss);
    const statusIcon = getStatusIcon(status);

    return (
      <Layout
        align="start"
        className={clsx('eds-banner', `eds-banner--status-${status}`)}
        role={getStatusLiveRole(status)}
        spacing="s"
      >
        <span className="eds-banner--status-icon">
          <Icon
            icon={statusIcon}
            label={intl.formatMessage(statusIconLabels[status])}
            size="m"
          />
        </span>
        <Layout direction="column" flex="auto" spacing="s">
          <div className="eds-banner--message-block">
            {title ? (
              <Text as="p" variant="body-semi-m">
                {title}
              </Text>
            ) : null}
            <Text as="p" variant="body-m">
              {text}
            </Text>
          </div>
          {link ? (
            <Link
              external={link.external}
              href={link.href}
              size={link.size ?? 'm'}
              text={link.text}
            />
          ) : null}
        </Layout>
        {action ? (
          <Button
            disabled={action.disabled}
            isLoading={action.isLoading}
            label={action.label}
            onClick={action.onClick}
            size="s"
            text={action.text}
            tooltip={action.tooltip}
            variant={action.variant ?? 'tertiary'}
          />
        ) : null}
        {onDismiss ? (
          <IconButton
            icon={{ key: 'x', label: dismissLabel }}
            label={dismissLabel}
            onClick={onDismiss}
            size="s"
            variant="tertiary"
          />
        ) : null}
      </Layout>
    );
  },
);

Banner.displayName = 'Banner';
